import { useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getApiErrorCode, getApiErrorField, getApiErrorMessage } from '@/client/utils/errors';
import type {
  AdminUserBulkAction,
  AdminUserBulkExtras,
  AdminUserBulkResult,
  UserAccountRoleType,
} from '@/client/types/admin/users-orgs';
import { BULK_ACTION_META, groupReasons, reasonLabel } from './bulk-actions';
import { roleTypeLabel } from './helpers';

type Phase = { kind: 'confirm' } | { kind: 'running' } | { kind: 'done'; result: AdminUserBulkResult } | { kind: 'error'; message: string };

const plural = (n: number) => `${n.toLocaleString()} user${n === 1 ? '' : 's'}`;
const things = (n: number, one: string) => `${n.toLocaleString()} ${one}${n === 1 ? '' : 's'}`;
/** Typed to release the second confirmation of a cascade. */
const CASCADE_PHRASE = 'RETAG';

function errorMessage(err: unknown): string {
  const code = getApiErrorCode(err);
  if (code === 'selection_changed') {
    const now = getApiErrorField<number>(err, 'total');
    return `The users matching your filters changed${now != null ? ` (now ${plural(now)})` : ''}. Nothing was updated — review the list and try again.`;
  }
  if (code === 'cascade_changed') {
    return 'The listings or organizations in scope changed since you reviewed them. Nothing was updated — review the impact again.';
  }
  if (code === 'too_many_users') {
    return `Too many users selected (max ${getApiErrorField<number>(err, 'max')?.toLocaleString() ?? '1,000'}). Narrow the filters.`;
  }
  return getApiErrorMessage(err, 'The bulk action failed. Nothing may have been updated.');
}

/** Confirm → run → per-outcome summary, for one bulk action. Mount with a `key` per open. */
export const BulkActionDialog: React.FC<{
  action: AdminUserBulkAction;
  count: number;
  roleTypes: UserAccountRoleType[];
  onRun: (extras: AdminUserBulkExtras) => Promise<AdminUserBulkResult>;
  onClose: () => void;
}> = ({ action, count, roleTypes, onRun, onClose }) => {
  const meta = BULK_ACTION_META[action];
  const [phase, setPhase] = useState<Phase>({ kind: 'confirm' });
  const [uartid, setUartid] = useState('');
  const [reason, setReason] = useState('');
  const [cascade, setCascade] = useState(false);
  // Second confirmation of a cascade: the dry run's counts, then a typed phrase.
  const [preview, setPreview] = useState<AdminUserBulkResult | null>(null);
  const [phrase, setPhrase] = useState('');

  const run = async () => {
    setPhase({ kind: 'running' });
    try {
      if (cascade && !preview) {
        setPreview(await onRun({ cascade: true, dry_run: true }));
        setPhrase('');
        setPhase({ kind: 'confirm' });
        return;
      }
      const result = await onRun({
        uartid: meta.needsRole ? Number(uartid) : undefined,
        reason: reason.trim() || undefined,
        ...(cascade ? { cascade: true, cascade_expected: preview?.cascade } : {}),
      });
      setPhase({ kind: 'done', result });
    } catch (err) {
      setPreview(null);
      setPhase({ kind: 'error', message: errorMessage(err) });
    }
  };

  const busy = phase.kind === 'running';

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent className="sm:max-w-lg">
        {phase.kind === 'done' ? (
          <Summary result={phase.result} onClose={onClose} />
        ) : preview ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-5 w-5" />
                Confirm retag of existing content
              </DialogTitle>
              <DialogDescription>
                {meta.label} — {plural(preview.updated.length)} will change status
                {preview.skipped.length > 0 ? `, ${preview.skipped.length.toLocaleString()} skipped` : ''}.
              </DialogDescription>
            </DialogHeader>

            <div role="alert" className="space-y-2 rounded-md border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-800">
              <p>
                <strong>{things(preview.cascade?.listings ?? 0, 'listing')}</strong> and{' '}
                <strong>{things(preview.cascade?.organizations ?? 0, 'organization')}</strong> will be{' '}
                {meta.cascadeEffect}.
              </p>
              <p>
                This includes every listing under organizations these users own, whoever posted it. Search follows
                within minutes. There is no one-click undo.
              </p>
            </div>

            <div className="space-y-1">
              <label htmlFor="bulk-cascade-phrase" className="text-sm font-medium text-gray-700">
                Type <span className="font-mono">{CASCADE_PHRASE}</span> to confirm
              </label>
              <input
                id="bulk-cascade-phrase"
                autoComplete="off"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={phrase}
                onChange={(e) => setPhrase(e.target.value)}
                disabled={busy}
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setPreview(null)} disabled={busy}>
                Back
              </Button>
              <Button variant="destructive" onClick={run} disabled={busy || phrase.trim() !== CASCADE_PHRASE}>
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Retag {things(preview.cascade?.listings ?? 0, 'listing')}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {meta.destructive && <AlertTriangle className="h-5 w-5 text-red-600" />}
                {meta.label} — {plural(count)}
              </DialogTitle>
              <DialogDescription>
                This will {meta.verb} <strong>{plural(count)}</strong>. {meta.note}
              </DialogDescription>
            </DialogHeader>

            {meta.needsRole && (
              <div className="space-y-1">
                <label htmlFor="bulk-role" className="text-sm font-medium text-gray-700">
                  Role
                </label>
                <select
                  id="bulk-role"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={uartid}
                  onChange={(e) => setUartid(e.target.value)}
                  disabled={busy}
                >
                  <option value="">Choose a role…</option>
                  {roleTypes.map((rt) => (
                    <option key={rt.uartid} value={rt.uartid}>
                      {roleTypeLabel(rt)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {meta.cascadeEffect && (
              <label className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={cascade}
                  onChange={(e) => setCascade(e.target.checked)}
                  disabled={busy}
                />
                <span>
                  <span className="flex items-center gap-1 font-medium">
                    <AlertTriangle className="h-4 w-4" /> Dangerous: also retag their existing listings and
                    organizations
                  </span>
                  Everything they already posted is {meta.cascadeEffect} at once. You will see the exact counts and
                  confirm again before anything changes.
                </span>
              </label>
            )}

            {meta.askReason && (
              <div className="space-y-1">
                <label htmlFor="bulk-reason" className="text-sm font-medium text-gray-700">
                  Reason{' '}
                  <span className="font-normal text-gray-400">
                    ({cascade ? 'required, ' : ''}recorded in the audit log)
                  </span>
                </label>
                <textarea
                  id="bulk-reason"
                  rows={2}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  disabled={busy}
                />
              </div>
            )}

            {phase.kind === 'error' && (
              <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                {phase.message}
              </p>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button
                variant={meta.destructive || cascade ? 'destructive' : 'default'}
                onClick={run}
                disabled={busy || (meta.needsRole && !uartid) || (cascade && !reason.trim())}
              >
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {cascade ? 'Review impact…' : `${meta.label} ${plural(count)}`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Summary: React.FC<{ result: AdminUserBulkResult; onClose: () => void }> = ({ result, onClose }) => {
  const skipped = groupReasons(result.skipped);
  const errors = groupReasons(result.errors);
  const ok = result.errors.length === 0;

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {ok ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <AlertTriangle className="h-5 w-5 text-amber-600" />}
          {BULK_ACTION_META[result.action].label}
          {result.role ? ` “${result.role.name}”` : ''} — done
        </DialogTitle>
        <DialogDescription>
          {plural(result.updated.length)} updated of {plural(result.requested)} selected.
        </DialogDescription>
      </DialogHeader>

      <dl className="space-y-3 text-sm">
        <Row label="Updated" value={result.updated.length} tone="text-emerald-700" />
        {result.cascade && (
          <>
            <Row label="Listings retagged" value={result.cascade.listings} tone="text-red-700" />
            <Row label="Organizations retagged" value={result.cascade.organizations} tone="text-red-700" />
          </>
        )}
        {skipped.length > 0 && (
          <div>
            <Row label="Skipped" value={result.skipped.length} tone="text-gray-700" />
            <ul className="mt-1 space-y-0.5 pl-4 text-xs text-gray-500">
              {skipped.map(([reason, n]) => (
                <li key={reason}>
                  {reasonLabel(reason)}: {n}
                </li>
              ))}
            </ul>
          </div>
        )}
        {errors.length > 0 && (
          <div>
            <Row label="Failed" value={result.errors.length} tone="text-red-700" />
            <ul className="mt-1 space-y-0.5 pl-4 text-xs text-red-600">
              {errors.map(([reason, n]) => (
                <li key={reason}>
                  {reasonLabel(reason)}: {n}
                </li>
              ))}
            </ul>
          </div>
        )}
      </dl>

      <DialogFooter>
        <Button onClick={onClose}>Done</Button>
      </DialogFooter>
    </>
  );
};

const Row: React.FC<{ label: string; value: number; tone: string }> = ({ label, value, tone }) => (
  <div className="flex justify-between">
    <dt className="text-gray-600">{label}</dt>
    <dd className={`font-semibold ${tone}`}>{value.toLocaleString()}</dd>
  </div>
);
